@if (activeCard$ | async; as activeCard) {
  <div class="max-w-md mx-auto p-1 pt-2 pb-12">
    <!-- Success Header -->
    <div class="text-center pt-2 pb-6 fade-in">
      <div
        class="w-16 h-16 mx-auto mb-5 rounded-full flex items-center justify-center bg-green-100"
      >
        <lucide-icon
          [img]="icons.CheckCircle"
          class="w-8 h-8 text-green-600"
        ></lucide-icon>
      </div>

      <h2 class="text-2xl font-bold text-gray-800 mb-2">Assignment Ready!</h2>
      <p class="text-gray-500 text-sm">
        Here's what you've done and what's next.
      </p>
    </div>

    <!-- Cards -->
    <div class="space-y-3 mt-2">
      <!-- Card 1: Assignment Created -->
      <div
        class="bg-white rounded-2xl border border-gray-100 shadow-sm transition-all duration-300 overflow-hidden"
      >
        <div class="flex items-center gap-4 p-4">
          <div
            class="shrink-0 w-9 h-9 rounded-full flex items-center justify-center bg-green-500 text-white transition-all duration-300"
          >
            <lucide-icon [img]="icons.Check" class="w-5 h-5"></lucide-icon>
          </div>
          <p class="font-semibold text-sm text-gray-800">
            Assignment created
          </p>
        </div>

        @if (activeCard >= 1) {
          <div class="px-4 pb-4 card-expand">
            <p class="text-sm text-gray-400">
              Your assignment has been successfully created and published. It is
              now live and accessible via a unique link.
            </p>
          </div>
        }
      </div>

      <!-- Card 2: Share with Students -->
      <div
        class="bg-white rounded-2xl border border-gray-100 shadow-sm transition-all duration-300 overflow-hidden"
      >
        <div class="flex items-center gap-4 p-4">
          @if (activeCard > 2) {
            <div
              class="shrink-0 w-9 h-9 rounded-full flex items-center justify-center bg-green-500 text-white transition-all duration-300"
            >
              <lucide-icon [img]="icons.Check" class="w-5 h-5"></lucide-icon>
            </div>
          } @else {
            <div
              class="shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300"
              [class.bg-amber-50]="activeCard >= 2"
              [class.text-amber-600]="activeCard >= 2"
              [class.bg-gray-100]="activeCard < 2"
              [class.text-gray-300]="activeCard < 2"
            >
              2
            </div>
          }
          <p
            class="font-semibold text-sm transition-colors duration-300"
            [class.text-gray-800]="activeCard >= 2"
            [class.text-gray-400]="activeCard < 2"
          >
            Share the link with students
          </p>
        </div>

        @if (activeCard >= 2) {
          <div class="px-4 pb-4 card-expand">
            <p class="text-sm text-gray-400">
              Copy and send this link so your students can start the assignment.
            </p>

            <!-- Share link -->
            <div class="flex items-center gap-2 mt-3">
              <div
                class="flex-1 px-3 py-2.5 bg-gray-50 rounded-xl text-gray-600 font-mono text-xs break-all select-all"
              >
                {{ displayLink$ | async }}
              </div>
              <button
                (click)="copyLink('card')"
                class="shrink-0 p-2.5 rounded-xl transition-all cursor-pointer"
                [class.bg-amber-500]="!(isCopied$ | async)"
                [class.bg-green-500]="isCopied$ | async"
              >
                @if (isCopied$ | async) {
                  <lucide-icon
                    [img]="icons.Check"
                    class="w-4 h-4 text-white"
                  ></lucide-icon>
                } @else {
                  <lucide-icon
                    [img]="icons.Copy"
                    class="w-4 h-4 text-white"
                  ></lucide-icon>
                }
              </button>
            </div>
          </div>
        }
      </div>

      <!-- Card 3: Test & Review -->
      <div
        class="bg-white rounded-2xl border border-gray-100 shadow-sm transition-all duration-300 overflow-hidden"
      >
        <div class="flex items-center gap-4 p-4">
          <div
            class="shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300"
            [class.bg-blue-50]="activeCard >= 3"
            [class.text-blue-600]="activeCard >= 3"
            [class.bg-gray-100]="activeCard < 3"
            [class.text-gray-300]="activeCard < 3"
          >
            3
          </div>
          <p
            class="font-semibold text-sm transition-colors duration-300"
            [class.text-gray-800]="activeCard >= 3"
            [class.text-gray-400]="activeCard < 3"
          >
            Test & review in dashboard
          </p>
        </div>

        @if (activeCard >= 3) {
          <div class="px-4 pb-4 card-expand">
            <p class="text-sm text-gray-400">
              Try the assignment yourself to make sure everything looks right,
              or head to the dashboard to track student submissions and grades.
            </p>
          </div>
        }
      </div>
    </div>

    <!-- Spacer for fixed footer -->
    <div class="h-24"></div>
  </div>

  <!-- Fixed Footer -->
  <div class="step-footer">
    <div class="max-w-md mx-auto flex gap-3 px-1">
      @switch (activeCard) {
        @case (1) {
          <button
            (click)="onContinue()"
            class="flex-1 py-3 text-white font-bold text-sm rounded-xl hover:opacity-90 transition-all cursor-pointer"
            [style.backgroundColor]="themeColor"
          >
            Next
          </button>
        }
        @case (2) {
          <button
            (click)="copyLink('footer')"
            class="flex-1 py-3 font-bold text-sm rounded-xl transition-all cursor-pointer border-2 hover:opacity-90"
            [style.borderColor]="themeColor"
            [style.color]="themeColor"
          >
            @if (isCopied$ | async) {
              Copied!
            } @else {
              Copy Link
            }
          </button>
          <button
            (click)="onContinue()"
            class="flex-[2] py-3 text-white font-bold text-sm rounded-xl hover:opacity-90 transition-all cursor-pointer"
            [style.backgroundColor]="themeColor"
          >
            Continue
          </button>
        }
        @case (3) {
          <button
            (click)="onTestAssignment()"
            class="flex-1 py-3 font-bold text-sm rounded-xl transition-all cursor-pointer border-2 hover:opacity-90 flex items-center justify-center gap-2"
            [style.borderColor]="themeColor"
            [style.color]="themeColor"
          >
            <lucide-icon
              [img]="icons.FlaskConical"
              class="w-4 h-4"
            ></lucide-icon>
            Test as Student
          </button>
          <span class="text-xs text-gray-400 font-medium self-center">or</span>
          <button
            (click)="onDone()"
            class="flex-1 py-3 text-white font-bold text-sm rounded-xl hover:opacity-90 transition-all cursor-pointer"
            [style.backgroundColor]="themeColor"
          >
            Finish
          </button>
        }
      }
    </div>
  </div>
}
